import React from 'react';
import { Box, TextField, Button, Link, InputAdornment, IconButton } from '@mui/material';
import { Person, Email, Lock, Visibility, VisibilityOff } from '@mui/icons-material';
import { LoginFormProps } from '../../shared/types';
import { useThemeMode } from '../../../../contexts/ThemeProvider';

const EmailLoginForm = ({ authLogic, config }: LoginFormProps) => {
  const { mode } = useThemeMode();
  const isDark = mode === 'dark';

  const { formData, errors, showPassword, isLoading, updateFormData, togglePasswordVisibility, goToForgotPassword, LOGIN_MODES } = authLogic;

  const loginMethodsConfig = config?.login?.loginMethods || {};
  const primaryColor = config?.theme?.primary || '#002465';

  const isEmail = (value: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);
  const inputValue = formData.email || '';
  const isEmailInput = isEmail(inputValue);

  const textFieldStyles = {
    input: {
      color: isDark ? '#ffffff' : 'black',
      '&:-webkit-autofill': {
        WebkitBoxShadow: isDark ? '0 0 0 1000px rgba(255, 255, 255, 0.05) inset !important' : '0 0 0 1000px white inset !important',
        WebkitTextFillColor: isDark ? '#ffffff !important' : 'black !important',
        transition: 'background-color 5000s ease-in-out 0s !important'
      },
      '&:-webkit-autofill:hover': {
        WebkitBoxShadow: isDark ? '0 0 0 1000px rgba(255, 255, 255, 0.05) inset !important' : '0 0 0 1000px white inset !important'
      },
      '&:-webkit-autofill:focus': {
        WebkitBoxShadow: isDark ? '0 0 0 1000px rgba(255, 255, 255, 0.05) inset !important' : '0 0 0 1000px white inset !important'
      }
    },
    mb: 3,
    '& .MuiOutlinedInput-root': {
      borderRadius: 2,
      backgroundColor: isDark ? 'rgba(255, 255, 255, 0.05)' : 'transparent',
      '&:hover .MuiOutlinedInput-notchedOutline': {
        borderColor: isDark ? '#ffffff' : '#d1d5db'
      },
      '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
        borderColor: isDark ? '#ffffff' : primaryColor,
        borderWidth: 2
      }
    },
    '& .MuiOutlinedInput-notchedOutline': {
      borderColor: isDark ? 'rgba(255, 255, 255, 0.3)' : '#e5e7eb'
    },
    '& .MuiInputLabel-root': {
      color: isDark ? 'rgba(255,255,255,0.7)' : '#6b7280',
      '&.Mui-focused': {
        color: isDark ? '#ffffff' : primaryColor
      }
    },
    '& .MuiFormHelperText-root': {
      color: '#f87171'
    }
  };

  return (
    <Box>
      <TextField
        fullWidth
        label="Email or Username"
        type="email"
        name="username"
        autoComplete="username"
        value={formData.email}
        onChange={e => updateFormData('email', e.target.value)}
        error={!!errors.email}
        helperText={errors.email}
        placeholder="Enter your email or username"
        disabled={isLoading}
        sx={textFieldStyles}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              {isEmailInput ? (
                <Email sx={{ color: isDark ? 'rgba(255,255,255,0.7)' : '#9ca3af', fontSize: 20 }} />
              ) : (
                <Person sx={{ color: isDark ? 'rgba(255,255,255,0.7)' : '#9ca3af', fontSize: 20 }} />
              )}
            </InputAdornment>
          )
        }}
      />

      <TextField
        fullWidth
        label="Password"
        type={showPassword ? 'text' : 'password'}
        name="password"
        autoComplete="current-password"
        value={formData.password}
        placeholder="Enter your password"
        onChange={e => updateFormData('password', e.target.value)}
        error={!!errors.password}
        helperText={errors.password}
        disabled={isLoading}
        sx={textFieldStyles}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <Lock sx={{ color: isDark ? 'rgba(255,255,255,0.7)' : '#9ca3af', fontSize: 20 }} />
            </InputAdornment>
          ),
          endAdornment: (
            <InputAdornment position="end">
              <IconButton
                onClick={togglePasswordVisibility}
                edge="end"
                sx={{
                  color: isDark ? 'rgba(255,255,255,0.7)' : '#9ca3af',
                  minWidth: { xs: '44px', md: '48px' },
                  minHeight: { xs: '44px', md: '48px' }
                }}
                disabled={isLoading}
              >
                {showPassword ? <VisibilityOff /> : <Visibility />}
              </IconButton>
            </InputAdornment>
          )
        }}
      />

      {loginMethodsConfig.forgotPassword?.enabled !== false && (
        <Box display="flex" justifyContent="flex-end" mb={4}>
          <Link
            component="button"
            type="button"
            variant="body2"
            onClick={goToForgotPassword}
            disabled={isLoading}
            sx={{
              color: isDark ? '#ffffff' : primaryColor,
              textDecoration: 'none',
              fontWeight: 500,
              cursor: isLoading ? 'not-allowed' : 'pointer',
              opacity: isLoading ? 0.6 : 1,
              minHeight: { xs: '44px', md: 'auto' },
              display: 'flex',
              alignItems: 'center',
              '&:hover': {
                textDecoration: isLoading ? 'none' : 'underline',
                opacity: isDark ? 0.8 : 1
              }
            }}
          >
            Forgot password?
          </Link>
        </Box>
      )}

      <Button
        type="submit"
        fullWidth
        variant="contained"
        size="large"
        disabled={isLoading}
        sx={{
          py: 2,
          fontSize: '1rem',
          fontWeight: 600,
          borderRadius: 2,
          textTransform: 'none',
          backgroundColor: isDark ? '#ffffff' : primaryColor,
          color: isDark ? '#000000' : '#ffffff',
          boxShadow: isDark ? '0 4px 12px rgba(255, 255, 255, 0.2)' : `0 4px 12px rgba(0, 36, 101, 0.2)`,
          cursor: isLoading ? 'not-allowed' : 'pointer',
          opacity: isLoading ? 0.7 : 1,
          '&:hover': {
            backgroundColor: isDark ? (isLoading ? '#ffffff' : 'rgba(255, 255, 255, 0.9)') : isLoading ? primaryColor : primaryColor,
            filter: isLoading ? 'none' : 'brightness(0.9)',
            boxShadow: isLoading
              ? isDark
                ? '0 4px 12px rgba(255, 255, 255, 0.2)'
                : `0 4px 12px rgba(0, 36, 101, 0.2)`
              : isDark
              ? '0 6px 16px rgba(255, 255, 255, 0.25)'
              : `0 6px 16px rgba(0, 36, 101, 0.25)`
          },
          '&:disabled': {
            backgroundColor: isDark ? '#ffffff' : primaryColor,
            color: isDark ? '#000000' : 'white'
          }
        }}
      >
        {isLoading ? (
          <Box display="flex" alignItems="center" gap={1}>
            <Box
              sx={{
                width: 20,
                height: 20,
                border: isDark ? '2px solid rgba(0,0,0,0.3)' : '2px solid rgba(255,255,255,0.3)',
                borderTop: isDark ? '2px solid black' : '2px solid white',
                borderRadius: '50%',
                animation: 'spin 1s linear infinite',
                '@keyframes spin': {
                  '0%': { transform: 'rotate(0deg)' },
                  '100%': { transform: 'rotate(360deg)' }
                }
              }}
            />
            Verifying credentials...
          </Box>
        ) : (
          'Sign in to Dashboard'
        )}
      </Button>
    </Box>
  );
};

export default EmailLoginForm;